import React, { useEffect } from 'react';
import { useGate, useStore } from 'effector-react';
import isEmpty from 'ramda/es/isEmpty';
import { toFlag } from 'pano-utils';
import type { Draft } from 'immer';
import type { STMap, STMapInitialView } from '@types';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { $stMapSettingPanelApi } from '@stores/sandTableStores';
import { $curSandTable, $tourDataApi } from '@stores/dataStores';
import Tip from '@components/Tip';
import ImageContainer from '@components/ImageContainer';
import LocationMap from '@components/LocationMap';
import FeatureToggle from '@components/FeatureToggle';
import useTourEditing from '@hooks/useTourEditing';
import { MapCaptureGate } from '@stores/editingMarkerStores';
import { Validator } from '@components/Validator';
import Button from '@components/Button';
import PanelBase from '../../panel/PanelBase';
import onMapCaptureClick from '../actions/onMapCaptureClick';
import { stEditorAmap } from '../STAMap';

const viewItems: DropdownItem<STMapInitialView['type']>[] = [
  { id: 0, name: '自动缩放显示全部标记点' },
  { id: 1, name: '自定义地图显示区域' },
];

const styleItems: DropdownItem<NonNullable<STMap['layerStyle']>>[] = [
  { id: 'normal', name: '标准' },
  { id: 'dark', name: '幻影黑' },
  { id: 'light', name: '月光银' },
  { id: 'whitesmoke', name: '远山黛' },
  { id: 'fresh', name: '草色青' },
  { id: 'grey', name: '雅士灰' },
  { id: 'graffiti', name: '涂鸦' },
  { id: 'macaron', name: '马卡龙' },
  { id: 'blue', name: '靛青蓝' },
  { id: 'darkblue', name: '极夜蓝' },
  { id: 'wine', name: '酱籽' },
];

export function PanelSandTableMapSetting() {
  const data = useStore($curSandTable) as STMap;
  const { state, setState, isChanged } = useTourEditing(data);
  const {
    initialView,
    showCurrentSceneOnly,
    layerType,
    layerStyle = 'normal',
  } = state;
  const { type: initialType, ...view } = initialView ?? {};

  useGate(MapCaptureGate, initialView?.type === 1);

  const hasView = (
    _view: typeof view
  ): _view is { lng: number; lat: number; zoom: number } => {
    return !isEmpty(_view);
  };

  useEffect(() => {
    return onMapCaptureClick.watch(() => {
      const center = stEditorAmap.getCenter();
      if (center) {
        setState((draft) => {
          draft.initialView = { type: 1, ...center };
        });
      }
    });
  }, [setState]);

  const handleLayerToggle = (isOn: boolean) => {
    const target = isOn ? 1 : 0;
    setState((draft) => {
      draft.layerType = target;
    });
  };
  const handleConfirm = () => {
    $tourDataApi.updateSandtable({
      id: data.id,
      updater: (draft: Draft<STMap>) => {
        draft.initialView = initialView;
        draft.showCurrentSceneOnly = showCurrentSceneOnly;
        draft.layerType = layerType;
        draft.layerStyle = layerStyle;
      },
    });
  };

  const handleStyleChange = (typeId: NonNullable<STMap['layerStyle']>) => {
    setState((draft) => {
      draft.layerStyle = typeId;
    });
    stEditorAmap.setMapStyle(typeId);
  };

  const renderAreaSetting = () => {
    if (initialType === 0) {
      return (
        <Tip className="mt-10">
          打开地图时，会自动缩放地图，以保证可以显示所有的标记点。
        </Tip>
      );
    }
    if (initialType === 1) {
      return (
        <>
          <Validator
            value={view}
            validFn={() => hasView(view)}
            warningMessage="请设置初始显示区域"
          >
            {({ value, warning }) => (
              <ImageContainer
                warning={warning}
                className="mt-20 h-115"
                placeholder={
                  <Tip className="text-center">
                    <p>通过设置左侧地图缩放级别</p>
                    <p>来设置初始显示区域</p>
                  </Tip>
                }
              >
                {hasView(view) && (
                  <>
                    <LocationMap
                      noMarker
                      location={value}
                      className="h-full w-full"
                    />
                    <Button
                      className="absolute"
                      scheme="gray"
                      onClick={() => {
                        stEditorAmap.setCenter(view);
                      }}
                    >
                      定位到初始区域
                    </Button>
                  </>
                )}
              </ImageContainer>
            )}
          </Validator>
          <Tip className="mt-10">
            打开地图时，会固定显示你所设置的初始区域，以保证用户可以看到最重要的内容。
          </Tip>
        </>
      );
    }
  };

  return (
    <PanelBase
      title="沙盘高级设置"
      isChanged={isChanged}
      onConfirm={handleConfirm}
      onRequestClose={$stMapSettingPanelApi.off}
    >
      <Collapse title="地图范围设置">
        <Dropdown
          items={viewItems}
          selected={initialType}
          onChange={(id) => {
            setState((draft) => {
              draft.initialView = { type: id } as STMapInitialView;
            });
            if (id === 0) {
              stEditorAmap.autoFit();
            }
          }}
        />
        {renderAreaSetting()}
      </Collapse>
      <Collapse title="地图模式设置">
        <div className="mb-10">地图样式</div>
        <Dropdown
          className="mb-20"
          selected={layerStyle}
          items={styleItems}
          onChange={handleStyleChange}
        />
        <FeatureToggle
          title="默认显示卫星地图"
          isOn={layerType === 1}
          onToggle={handleLayerToggle}
        />
        <FeatureToggle
          title="缩略图仅显示当前场景"
          isOn={!!showCurrentSceneOnly}
          onToggle={(isOn) => {
            setState((draft) => {
              draft.showCurrentSceneOnly = toFlag(isOn);
            });
          }}
        />
        <Tip className="mt-10">
          在场景沙盘缩略图中，只显示当前场景的标记点，以突出重点。
        </Tip>
      </Collapse>
    </PanelBase>
  );
}
